import type {
  AdEntityStatus,
  AdPlatform,
  AlertSeverity,
  AlertStatus,
  AlertType,
  AttributionChannel,
  AudienceType,
  CampaignObjective,
  CancelReason,
  Channel,
  ConfirmationState,
  ConversationStatus,
  CustomerSource,
  ExperimentStatus,
  ExperimentType,
  FollowupStatus,
  FollowupType,
  LeadStatus,
  MessageStatus,
  OrderSource,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  PlaybookAction,
  PlaybookTrigger,
  RecommendationStatus,
  RecommendationType,
  ReturnStatus,
  ReturnType,
  Role,
  SettlementStatus,
  ShipmentStatus,
  SubscriptionStatus,
  CodState,
  AiProvider,
  AiFeature,
  RiskClass,
  BlockType,
  ReportType,
  SiteStatus,
  DomainStatus,
} from "./enums";

// French UI labels. Keep them short: they are shown in pills, tabs and table cells.

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Administrateur",
  manager: "Manager",
  media_buyer: "Media buyer",
  agent: "Agent",
  viewer: "Lecteur",
};

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: "Nouvelle",
  confirmed: "Confirmée",
  preparing: "En préparation",
  shipped: "En transit",
  delivered: "Livrée",
  returned: "Retournée",
  cancelled: "Annulée",
};

export const LATE_LABEL = "En retard";

export const CONFIRMATION_STATE_LABELS: Record<ConfirmationState, string> = {
  pending: "À confirmer",
  requested: "Confirmation envoyée",
  no_answer: "Pas de réponse",
  postponed: "Reportée",
  confirmed: "Confirmée",
  refused: "Refusée",
  unreachable: "Injoignable",
};

export const CANCEL_REASON_LABELS: Record<CancelReason, string> = {
  customer_refused: "Refus du client",
  unreachable: "Client injoignable",
  duplicate: "Doublon",
  fake_order: "Fausse commande",
  out_of_stock: "Rupture de stock",
  customer_request: "Demande du client",
  address_issue: "Adresse invalide",
  other: "Autre",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cod: "Paiement à la livraison",
  card: "Carte bancaire",
  bank_transfer: "Virement",
  other: "Autre",
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: "Non payée",
  paid: "Payée",
  partially_refunded: "Remboursée en partie",
  refunded: "Remboursée",
};

export const COD_STATE_LABELS: Record<CodState, string> = {
  none: "Sans COD",
  pending: "À encaisser",
  collected: "Encaissé par le transporteur",
  settled: "Versé",
};

export const ORDER_SOURCE_LABELS: Record<OrderSource, string> = {
  manual: "Manuelle",
  whatsapp: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
  website_chat: "Chat du site",
  landing_form: "Landing page",
  meta_lead: "Meta Lead Ads",
  tiktok_lead: "TikTok Lead",
  import: "Import",
  phone: "Téléphone",
  api: "API",
};

export const ATTRIBUTION_CHANNEL_LABELS: Record<AttributionChannel, string> = {
  meta_ads: "Meta Ads",
  tiktok_ads: "TikTok Ads",
  google_ads: "Google Ads",
  organic: "Organique",
  other: "Autres",
};

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  pending: "En préparation",
  picked_up: "Enlevé",
  in_transit: "En transit",
  out_for_delivery: "En cours de livraison",
  delivered: "Livré",
  failed_attempt: "Tentative échouée",
  returning: "Retour en cours",
  returned: "Retourné",
  cancelled: "Annulé",
  exception: "Incident",
  unknown: "Statut inconnu",
};

export const CHANNEL_LABELS: Record<Channel, string> = {
  whatsapp: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
  website: "Site web",
};

export const CUSTOMER_SOURCE_LABELS: Record<CustomerSource, string> = {
  whatsapp: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
  website: "Site web",
  landing_form: "Landing page",
  meta_lead: "Meta Lead Ads",
  tiktok_lead: "TikTok Lead",
  manual: "Manuel",
  import: "Import",
  phone: "Téléphone",
};

export const CONVERSATION_STATUS_LABELS: Record<ConversationStatus, string> = {
  open: "Ouverte",
  pending: "En attente",
  resolved: "Traitée",
  snoozed: "En pause",
};

export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  received: "Reçu",
  queued: "En file",
  sent: "Envoyé",
  delivered: "Distribué",
  read: "Lu",
  failed: "Échec",
};

export const AD_PLATFORM_LABELS: Record<AdPlatform, string> = {
  meta: "Meta Ads",
  tiktok: "TikTok Ads",
  google: "Google Ads",
};

export const CAMPAIGN_OBJECTIVE_LABELS: Record<CampaignObjective, string> = {
  sales: "Ventes",
  leads: "Leads",
  traffic: "Trafic",
  awareness: "Notoriété",
  engagement: "Engagement",
  messages: "Messages",
  app_promotion: "Application",
  video_views: "Vues de vidéo",
};

export const AD_ENTITY_STATUS_LABELS: Record<AdEntityStatus, string> = {
  draft: "Brouillon",
  active: "Active",
  paused: "En pause",
  in_review: "En examen",
  rejected: "Refusée",
  with_issues: "À corriger",
  completed: "Terminée",
  archived: "Archivée",
  deleted: "Supprimée",
  error: "Erreur",
};

export const AUDIENCE_TYPE_LABELS: Record<AudienceType, string> = {
  customer_list: "Liste clients",
  website: "Visiteurs du site",
  engagement: "Engagement",
  lookalike: "Similaire",
  saved: "Enregistrée",
  other: "Autre",
};

export const RECOMMENDATION_TYPE_LABELS: Record<RecommendationType, string> = {
  increase_budget: "Augmenter le budget",
  decrease_budget: "Réduire le budget",
  pause_entity: "Mettre en pause",
  activate_entity: "Activer",
  duplicate_winner: "Dupliquer le gagnant",
  refresh_creative: "Renouveler la création",
  new_audience: "Tester une audience",
  lookalike_audience: "Audience similaire",
  retargeting: "Retargeting",
  fix_tracking: "Corriger le suivi",
  shift_budget: "Réallouer le budget",
  stop_loss: "Arrêter les pertes",
  test_creative: "Tester une création",
  pause_out_of_stock: "Produit en rupture",
  insight: "Observation",
};

export const RECOMMENDATION_STATUS_LABELS: Record<RecommendationStatus, string> = {
  open: "À traiter",
  approved: "Approuvée",
  rejected: "Refusée",
  executing: "En cours",
  executed: "Exécutée",
  failed: "Échec",
  expired: "Expirée",
  dismissed: "Ignorée",
};

export const RISK_CLASS_LABELS: Record<RiskClass, string> = {
  safe: "Sans risque",
  external: "Action externe",
  financial: "Action financière",
};

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  spend_spike: "Pic de dépenses",
  spend_drop: "Chute de dépenses",
  roas_drop: "Baisse du ROAS",
  cpa_spike: "Hausse du CPA",
  ctr_drop: "Baisse du CTR",
  frequency_high: "Fréquence élevée",
  creative_fatigue: "Fatigue créative",
  ad_rejected: "Publicité refusée",
  account_disabled: "Compte désactivé",
  budget_exhausted: "Budget épuisé",
  delivery_stopped: "Diffusion arrêtée",
  token_expired: "Connexion expirée",
  pixel_inactive: "Pixel inactif",
  stockout_with_active_ads: "Rupture avec pubs actives",
  learning_limited: "Apprentissage limité",
  guardrail_breach: "Garde-fou dépassé",
};

export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info: "Info",
  warning: "Attention",
  critical: "Critique",
};

export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  open: "Ouverte",
  acknowledged: "Prise en compte",
  resolved: "Résolue",
};

export const EXPERIMENT_TYPE_LABELS: Record<ExperimentType, string> = {
  creative: "Création",
  audience: "Audience",
  budget: "Budget",
  placement: "Placement",
  landing_page: "Landing page",
  offer: "Offre",
};

export const EXPERIMENT_STATUS_LABELS: Record<ExperimentStatus, string> = {
  draft: "Brouillon",
  running: "En cours",
  completed: "Terminée",
  stopped: "Arrêtée",
};

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: "Nouveau",
  contacted: "Contacté",
  qualified: "Qualifié",
  converted: "Converti",
  lost: "Perdu",
};

export const RETURN_TYPE_LABELS: Record<ReturnType, string> = {
  rto: "Retour transporteur",
  customer_return: "Retour client",
  exchange: "Échange",
};

export const RETURN_STATUS_LABELS: Record<ReturnStatus, string> = {
  expected: "Attendu",
  in_transit: "En transit",
  received: "Reçu",
  restocked: "Remis en stock",
  closed: "Clôturé",
};

export const SETTLEMENT_STATUS_LABELS: Record<SettlementStatus, string> = {
  pending: "En attente",
  partial: "Partiel",
  settled: "Réglé",
  disputed: "Litige",
};

export const FOLLOWUP_TYPE_LABELS: Record<FollowupType, string> = {
  order_confirmation: "Confirmation de commande",
  unconfirmed_order: "Commande non confirmée",
  abandoned_form: "Formulaire abandonné",
  failed_delivery: "Livraison échouée",
  shipping_update: "Suivi de livraison",
  review_request: "Demande d'avis",
  replenishment: "Réapprovisionnement",
  win_back: "Reconquête",
  payment_reminder: "Rappel de paiement",
  custom: "Personnalisée",
};

export const FOLLOWUP_STATUS_LABELS: Record<FollowupStatus, string> = {
  scheduled: "Planifiée",
  sent: "Envoyée",
  failed: "Échec",
  cancelled: "Annulée",
  replied: "Répondue",
  skipped: "Ignorée",
};

export const PLAYBOOK_TRIGGER_LABELS: Record<PlaybookTrigger, string> = {
  "order.created": "Commande créée",
  "order.confirmed": "Commande confirmée",
  "order.cancelled": "Commande annulée",
  "order.status_changed": "Statut de commande modifié",
  "shipment.status_changed": "Statut de livraison modifié",
  "message.received": "Message reçu",
  "conversation.created": "Nouvelle conversation",
  "lead.created": "Nouveau lead",
  "customer.created": "Nouveau client",
  "form.abandoned": "Formulaire abandonné",
  "product.low_stock": "Stock faible",
  "ads.alert": "Alerte publicité",
  "schedule.daily": "Chaque jour",
};

export const PLAYBOOK_ACTION_LABELS: Record<PlaybookAction, string> = {
  send_message: "Envoyer un message",
  send_template: "Envoyer un modèle WhatsApp",
  ai_reply: "Réponse IA",
  create_shipment: "Créer l'expédition",
  set_order_status: "Changer le statut de commande",
  request_confirmation: "Demander la confirmation",
  add_tag: "Ajouter un tag",
  remove_tag: "Retirer un tag",
  assign_conversation: "Assigner la conversation",
  set_conversation_status: "Changer le statut de conversation",
  create_followup: "Planifier une relance",
  notify_team: "Notifier l'équipe",
  webhook: "Appeler un webhook",
  wait: "Attendre",
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trialing: "Essai",
  active: "Actif",
  past_due: "Paiement en retard",
  canceled: "Résilié",
  expired: "Expiré",
};

export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  anthropic: "Claude (Anthropic)",
  openai: "OpenAI",
  gemini: "Google Gemini",
};

export const AI_FEATURE_LABELS: Record<AiFeature, string> = {
  assistant: "Assistant IA",
  autoreply: "Réponses automatiques",
  media_buyer: "AI Media Buyer",
  insights: "Insights IA",
  content: "Génération de contenu",
  brain_playground: "Test du Business Brain",
  recommendations: "Recommandations",
};

export const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  hero: "Bannière principale",
  product: "Produit",
  benefits: "Avantages",
  features: "Caractéristiques",
  testimonials: "Avis clients",
  faq: "Questions fréquentes",
  order_form: "Formulaire de commande",
  lead_form: "Formulaire de contact",
  countdown: "Compte à rebours",
  gallery: "Galerie",
  video: "Vidéo",
  cta: "Appel à l'action",
  text: "Texte",
  image: "Image",
  trust_badges: "Garanties",
  bundle_offer: "Offre groupée",
  whatsapp_button: "Bouton WhatsApp",
  spacer: "Espace",
  footer: "Pied de page",
};

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  sales: "Ventes",
  orders: "Commandes",
  deliveries: "Livraisons",
  ads: "Publicité",
  customers: "Clients",
  products: "Produits",
  conversations: "Conversations",
  cod: "Encaissements",
};

export const SITE_STATUS_LABELS: Record<SiteStatus, string> = {
  draft: "Brouillon",
  published: "Publié",
  archived: "Archivé",
};

export const DOMAIN_STATUS_LABELS: Record<DomainStatus, string> = {
  pending: "En attente",
  verified: "Vérifié",
  failed: "Échec",
};
